import React, { useState, useEffect, useMemo } from 'react';
import { logError } from '../lib/logger';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Send, Info } from 'lucide-react';
import { QUOTE_PRODUCTS, QUOTE_SOURCES, OTHER_PRODUCT, resolveQuoteProduct } from '../data/quoteProducts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  sources: [],
  lockedSources: null,
  products: ['B00033'],
  otherRequest: '',
  name: '',
  email: '',
  company: '',
  companyDescription: '',
  phone: '',
  message: '',
  mailingListOptIn: false,
};

const RequestQuoteModal = ({ open, onOpenChange, prefill }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sourceError, setSourceError] = useState(false);
  const [productError, setProductError] = useState(false);
  const [formData, setFormData] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    const locked = prefill?.lockedSources?.length ? prefill.lockedSources : null;
    const { product, other } = resolveQuoteProduct(prefill?.product);
    setFormData((f) => ({
      ...f,
      products: prefill?.product ? [product] : f.products.filter((p) => !locked || p !== OTHER_PRODUCT),
      otherRequest: other || f.otherRequest,
      sources: locked || prefill?.sources || [],
      lockedSources: locked,
      message: prefill?.message || f.message,
    }));
  }, [open, prefill]);

  const locked = formData.lockedSources;
  const lockedLabels = useMemo(() => (locked ? QUOTE_SOURCES.filter((s) => locked.includes(s.id)).map((s) => s.label) : []), [locked]);
  const lockedSubs = useMemo(() => (locked ? QUOTE_SOURCES.filter((s) => locked.includes(s.id)).map((s) => s.sub).join(' · ') : ''), [locked]);
  const productOptions = useMemo(() => (locked ? QUOTE_PRODUCTS : [...QUOTE_PRODUCTS, { pn: OTHER_PRODUCT, label: 'Other' }]), [locked]);

  const catalog = formData.products.filter((p) => p !== OTHER_PRODUCT);
  const hasOther = formData.products.includes(OTHER_PRODUCT);
  const otherOnly = hasOther && catalog.length === 0;

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const toggleSource = (id) => {
    setSourceError(false);
    setFormData((f) => ({ ...f, sources: f.sources.includes(id) ? f.sources.filter((s) => s !== id) : [...f.sources, id] }));
  };

  const toggleProduct = (pn) => {
    setProductError(false);
    setFormData((f) => ({ ...f, products: f.products.includes(pn) ? f.products.filter((p) => p !== pn) : [...f.products, pn] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.products.length === 0) {
      setProductError(true);
      document.getElementById('quote-products')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!otherOnly && !locked && formData.sources.length === 0) {
      setSourceError(true);
      document.getElementById('quote-sources')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setIsSubmitting(true);
    const ordered = [...QUOTE_PRODUCTS.filter((p) => formData.products.includes(p.pn)), ...(hasOther ? [{ pn: OTHER_PRODUCT, label: 'Other' }] : [])];
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, {
        form_type: 'Request Quote',
        name: formData.name,
        email: formData.email,
        company: formData.company,
        company_description: formData.companyDescription,
        phone: formData.phone,
        message: formData.message,
        product: ordered.map((p) => p.label).join(', '),
        product_pn: catalog.length ? ordered.filter((p) => p.pn !== OTHER_PRODUCT).map((p) => p.pn).join(', ') : null,
        products: ordered.map((p) => ({ pn: p.pn, label: p.label })),
        other_request: hasOther ? formData.otherRequest : null,
        sources: otherOnly ? ['ctrl'] : (locked || formData.sources),
        mailing_list_opt_in: formData.mailingListOptIn,
      });

      if (response.data.success) {
        toast.success(response.data.message || "Thank you! A copy of your request is on its way to your inbox.");
        window.dispatchEvent(new CustomEvent('ctrl:quote-submitted', { detail: { product: formData.products.join(','), parts: /^Replacement parts request/.test(formData.message) } }));
        setFormData(EMPTY);
        onOpenChange(false);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
      logError('Form submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit form. Please try again or contact us directly at info@ctrlsys.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#1e2d3b]">{locked ? `Request a Quote from ${lockedLabels.join(' & ')}` : 'Request a Quote'}</DialogTitle>
          <DialogDescription className="text-slate-600">
            {locked
              ? `Your request goes directly to ${lockedLabels.join(' & ')} with CTRL Systems in copy. Pick the products you'd like quoted and tell us about your operation — you'll receive a copy by email.`
              : "Choose who should quote you, pick one or more products, and tell us about your operation. Separate requests go to each source you select, and you'll receive a copy by email."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Step 1 — quote sources */}
          {locked ? (
            <div className="flex items-start gap-3 p-4 bg-orange-50/60 border border-orange-100 rounded-lg" data-testid="quote-locked-source-note">
              <Info className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-slate-700">
                <span className="font-semibold text-[#1e2d3b]">Quote source: {lockedLabels.join(' & ')}</span> — CTRL Systems is copied on your request. {lockedSubs}
              </p>
            </div>
          ) : otherOnly ? (
            <div className="flex items-start gap-3 p-4 bg-orange-50/60 border border-orange-100 rounded-lg" data-testid="quote-other-routing-note">
              <Info className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-slate-700">
                <span className="font-semibold text-[#1e2d3b]">"Other" requests route to CTRL Systems only.</span> They are not sent to Barfield or AvionTEq — our team will review and point you to the right source.
              </p>
            </div>
          ) : (
            <div id="quote-sources" className={`space-y-3 p-4 rounded-lg border ${sourceError ? 'bg-red-50 border-red-300' : 'bg-orange-50/60 border-orange-100'}`} data-testid="quote-partner-routing">
              <Label className="text-base font-semibold text-[#1e2d3b]">1. Send my quote request to *</Label>
              <p className="text-xs text-slate-600">Select one or more. Each source receives its own request — they never see each other.</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {QUOTE_SOURCES.map((opt) => {
                  const on = formData.sources.includes(opt.id);
                  return (
                    <label key={opt.id} className={`flex flex-col rounded-md border px-3 py-2 cursor-pointer transition-colors ${on ? 'border-orange-500 bg-white shadow-sm' : 'border-slate-200 bg-white/60 hover:border-orange-300'}`} data-testid={`quote-source-${opt.id}`}>
                      <span className="flex items-center gap-2">
                        <input type="checkbox" checked={on} onChange={() => toggleSource(opt.id)} className="w-4 h-4 text-orange-600 rounded" data-testid={`quote-source-checkbox-${opt.id}`} />
                        <span className="text-sm font-semibold text-[#1e2d3b]">{opt.label}</span>
                      </span>
                      <span className="pl-6 text-[11px] text-slate-500">{opt.sub}</span>
                    </label>
                  );
                })}
              </div>
              {sourceError && <p className="text-xs font-semibold text-red-600" data-testid="quote-source-error">Please choose at least one source for your quote.</p>}
              {hasOther && <p className="text-xs text-slate-600" data-testid="quote-other-mixed-note"><Info className="inline h-3.5 w-3.5 text-orange-600 mr-1 -mt-0.5" aria-hidden="true" />Your "Other" item goes to CTRL Systems only; the products above go to the sources you select.</p>}
            </div>
          )}

          {/* Step 2 — products (multi-select) */}
          <div id="quote-products" className={`space-y-3 p-4 rounded-lg ${productError ? 'bg-red-50 border border-red-300' : 'bg-slate-50'}`} data-testid="quote-product-selection">
            <Label className="text-base font-semibold text-[#1e2d3b]">{locked ? '1. Products *' : '2. Products *'}</Label>
            <p className="text-xs text-slate-600">Select every product you'd like quoted.</p>
            <div className="space-y-2">
              {productOptions.map((p) => (
                <div key={p.pn} className="flex items-center space-x-2">
                  <input type="checkbox" id={`product-${p.pn}`} checked={formData.products.includes(p.pn)} onChange={() => toggleProduct(p.pn)} className="w-4 h-4 text-orange-600 rounded" data-testid={`quote-product-${p.pn}`} />
                  <Label htmlFor={`product-${p.pn}`} className="font-normal cursor-pointer">
                    {p.label}{p.pn !== OTHER_PRODUCT && <span className="ml-1.5 font-mono text-xs text-slate-500">({p.pn})</span>}
                  </Label>
                </div>
              ))}
            </div>
            {productError && <p className="text-xs font-semibold text-red-600" data-testid="quote-product-error">Please select at least one product.</p>}
            {hasOther && (
              <div className="space-y-2 pt-1">
                <Label htmlFor="otherRequest">Tell us what you need *</Label>
                <Textarea id="otherRequest" name="otherRequest" value={formData.otherRequest} onChange={handleInputChange} required placeholder="Describe the product, accessory or service you're looking for…" rows={3} className="border-slate-300 focus:border-orange-500 resize-none bg-white" data-testid="quote-other-request" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleInputChange} required placeholder="John Doe" className="border-slate-300 focus:border-blue-500" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required placeholder="john@company.com" className="border-slate-300 focus:border-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company">Company Name *</Label>
              <Input id="company" name="company" value={formData.company} onChange={handleInputChange} required placeholder="Your Company" className="border-slate-300 focus:border-blue-500" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="+1 (555) 123-4567" className="border-slate-300 focus:border-blue-500" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="companyDescription">Company Type *</Label>
            <select id="companyDescription" name="companyDescription" value={formData.companyDescription} onChange={handleInputChange} required className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500">
              <option value="">Select company type...</option>
              <option value="airline-operator">Airline/Operator</option>
              <option value="aircraft-mro">Aircraft MRO</option>
              <option value="aircraft-oem">Aircraft OEM</option>
              <option value="component-oem">Component OEM</option>
              <option value="component-repair">Component Repair/Overhaul Shop</option>
              <option value="fbo">FBO</option>
              <option value="line-maintenance">Line Maintenance Provider</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Quote Requirements</Label>
            <Textarea id="message" name="message" value={formData.message} onChange={handleInputChange} placeholder="Quantities, delivery timing, calibration needs, or any specific requirements..." rows={4} className="border-slate-300 focus:border-blue-500 resize-none" />
          </div>

          <label className="flex items-start gap-3 rounded-lg bg-slate-50 p-3 border border-slate-200 cursor-pointer">
            <input type="checkbox" required data-testid="quote-optin-checkbox" checked={formData.mailingListOptIn} onChange={(e) => setFormData({ ...formData, mailingListOptIn: e.target.checked })} className="mt-1 h-4 w-4 text-orange-600 rounded border-slate-300 focus:ring-orange-500" />
            <span className="text-sm text-slate-700 leading-snug">
              <span className="text-red-600 font-semibold">*</span> Please add me to the ctrl.aero mailing list for product updates, case studies, and aviation maintenance insights. Required to submit.
            </span>
          </label>

          <Button type="submit" disabled={isSubmitting} data-testid="quote-submit-btn" className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-3 text-[15px]">
            {isSubmitting ? 'Submitting...' : (<><Send className="mr-2 h-5 w-5" /> Request Quote</>)}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RequestQuoteModal;
